// Libs
import React from 'react';
import PropTypes from 'prop-types';

// Components
import ContextMenuItem from './ContextMenuItem';
import ContextMenuDivider from './ContextMenuDivider';
import BoardBackgroundContextMenuItems from './BoardBackgroundContextMenuItems';
import UserFlagFeature from '../../components/visibility/UserFlagFeature';

// Constants
import { BoardSections, RootBoardTypes } from '../../../common/boards/boardConstants';
import { ExperimentId } from '../../../common/experiments/experimentsConstants';

const CanvasContextMenuItems = ({
    currentFocus,
    showPaste,
    showUndo,
    showRedo,
    showSelectAll,
    triggerMenuItemOnClick,
    paste,
    registerMenuItem,
    createNewCard,
    createNewLink,
    createNewLine,
    createNewBoard,
    createNewColumn,
    createNewComment,
    createNewTaskList,
    createNewTable,
    createNewImageOrFile,
    createNewPublicTemplate,
    dispatchUndoAction,
    dispatchRedoAction,
    dispatchSelectAll,
    openBackgroundColorPicker,
    isSidebarPrototypeEnabled,
    createNewFolder,
}) => {
    const contextMenuHandlers = {
        registerMenuItem,
        onMouseUp: triggerMenuItemOnClick,
    };

    return (
        <div>
            <ContextMenuItem
                {...contextMenuHandlers}
                title="Paste"
                id="paste"
                shortcut={['command', 'v']}
                show={showPaste}
                onClick={paste}
            />
            <ContextMenuDivider show={showPaste} />
            <ContextMenuItem
                {...contextMenuHandlers}
                title="Undo"
                id="undo"
                shortcut={['command', 'z']}
                show={showUndo}
                onClick={dispatchUndoAction}
            />
            <ContextMenuItem
                {...contextMenuHandlers}
                title="Redo"
                id="redo"
                shortcut={['command', 'shift', 'z']}
                show={showRedo}
                onClick={dispatchRedoAction}
            />
            <ContextMenuDivider show={showUndo || showRedo} />
            <ContextMenuItem
                {...contextMenuHandlers}
                title="Select All"
                shortcut={['command', 'a']}
                id="selectAll"
                show={showSelectAll}
                onClick={dispatchSelectAll}
            />
            <ContextMenuDivider show={showSelectAll} />
            <ContextMenuItem {...contextMenuHandlers} title="New Note" id="newNote" show onClick={createNewCard} />
            <ContextMenuItem {...contextMenuHandlers} title="New Link" id="newLink" show onClick={createNewLink} />
            <ContextMenuItem
                {...contextMenuHandlers}
                title="New To-do"
                id="newTaskList"
                show
                onClick={createNewTaskList}
            />
            <ContextMenuItem
                {...contextMenuHandlers}
                title="New Line"
                id="newLine"
                show={currentFocus === BoardSections.CANVAS}
                onClick={createNewLine}
            />
            <ContextMenuItem
                {...contextMenuHandlers}
                title={isSidebarPrototypeEnabled ? 'New Folder' : 'New Board'}
                id="newBoard"
                show={currentFocus !== RootBoardTypes.QUICK_NOTES}
                onClick={isSidebarPrototypeEnabled ? createNewFolder : createNewBoard}
            />
            <ContextMenuItem
                {...contextMenuHandlers}
                title="New Column"
                id="newColumn"
                show={currentFocus !== RootBoardTypes.QUICK_NOTES}
                onClick={createNewColumn}
            />
            <ContextMenuItem
                {...contextMenuHandlers}
                title="New Comment"
                id="newComment"
                show={currentFocus !== RootBoardTypes.QUICK_NOTES}
                onClick={createNewComment}
            />
            <ContextMenuItem {...contextMenuHandlers} title="New Table" id="newTable" show onClick={createNewTable} />

            <ContextMenuDivider show />
            <BoardBackgroundContextMenuItems
                contextMenuHandlers={contextMenuHandlers}
                openBackgroundColorPicker={openBackgroundColorPicker}
            />
            <ContextMenuDivider show={currentFocus !== RootBoardTypes.QUICK_NOTES} />
            <ContextMenuItem
                {...contextMenuHandlers}
                title="New Image/File Placeholder"
                id="newImageOrFilePlaceholder"
                show={currentFocus !== RootBoardTypes.QUICK_NOTES}
                onClick={createNewImageOrFile}
            />
            <UserFlagFeature featureName={ExperimentId.publicTemplateCreation}>
                <ContextMenuDivider show={currentFocus !== RootBoardTypes.QUICK_NOTES} />
                <ContextMenuItem
                    {...contextMenuHandlers}
                    title="New Public Template"
                    id="createNewPublicTemplate"
                    show={currentFocus !== RootBoardTypes.QUICK_NOTES}
                    onClick={createNewPublicTemplate}
                />
            </UserFlagFeature>
        </div>
    );
};

CanvasContextMenuItems.propTypes = {
    location: PropTypes.object,
    rootBoardId: PropTypes.string,
    currentBoardId: PropTypes.string,
    currentFocus: PropTypes.string,
    showPaste: PropTypes.bool,
    showUndo: PropTypes.bool,
    showRedo: PropTypes.bool,
    showSelectAll: PropTypes.bool,
    isSidebarPrototypeEnabled: PropTypes.bool,
    triggerMenuItemOnClick: PropTypes.func,
    paste: PropTypes.func,
    registerMenuItem: PropTypes.func,
    createNewCard: PropTypes.func,
    createNewLink: PropTypes.func,
    createNewTable: PropTypes.func,
    createNewLine: PropTypes.func,
    createNewBoard: PropTypes.func,
    createNewColumn: PropTypes.func,
    createNewComment: PropTypes.func,
    createNewTaskList: PropTypes.func,
    createNewImageOrFile: PropTypes.func,
    createNewFolder: PropTypes.func,
    createNewPublicTemplate: PropTypes.func,
    dispatchPasteElements: PropTypes.func,
    dispatchUndoAction: PropTypes.func,
    dispatchRedoAction: PropTypes.func,
    dispatchSelectAll: PropTypes.func,
    dispatchCreateElement: PropTypes.func,
    openBackgroundColorPicker: PropTypes.func,
};

export default CanvasContextMenuItems;
